import { addDays, differenceInCalendarDays, differenceInDays, format, getYear, isSameDay, isToday, isTomorrow, parseISO, set, startOfDay } from 'date-fns';
import { CmsValue } from 'core/type/BootstrapConfig.types';

export const MILLISECOND_PER_DAY = 1000 * 60 * 60 * 24;
export const toTimeOnly = (date: Date) => format(date, 'HH:mm');
export const now = () => Date.now();

export const getDateWithoutTime = (date: Date) => {
    return startOfDay(date);
};

export const daysFromToday = (date: string, today = new Date()) => {
    if (!date) return '';
    const date1 = getDateWithoutTime(parseISO(date));
    const date2 = getDateWithoutTime(today);
    const days = differenceInDays(date1, date2);
    return Number.isNaN(days) ? '' : String(days);
};

const todayDate = new Date(Date.now());
const tomorrowDate = addDays(todayDate, 1);
const tomorrowPlusOneDate = addDays(tomorrowDate, 1);

export const getTodayDate = () => todayDate;
export const getTomorrowDate = () => tomorrowDate;
export const getTomorrowPlusOneDate = () => tomorrowPlusOneDate;

const CutOffTimeForSelectingTomorrow = 17;
export const getSelectedDate = (tomorrow: Date = getTomorrowDate(), tomorrowPlusOne: Date = getTomorrowPlusOneDate(), date = new Date()): Date => {
    const hours = date.getHours();
    if (hours >= CutOffTimeForSelectingTomorrow) {
        return tomorrowPlusOne;
    }
    return tomorrow;
};

export const getDateInYmdFormat = (date?: Date): string => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
};

export const getDateFromString = (dateString?: string, fallback?: Date): Date | undefined => {
    if (!dateString) return fallback;
    return parseISO(dateString);
};

export const getTranslatedMonthYear = (cms: CmsValue, date: string): string => {
    if (!date) return '';
    const translatedMonth = cms[71411].split(',');
    const reviewDate = parseISO(date);
    const reviewMonth = reviewDate.getMonth();
    return `${translatedMonth[reviewMonth]} ${reviewDate.getFullYear()}`;
};
export const getFormattedDate = (date: Date, cms: CmsValue, includeYear = true, showDayOfWeek = true) => {
    const days = date.getDate();
    const years = getYear(date);
    const translatedWeekdays = cms[71412].split(',');
    const dayName = translatedWeekdays[date.getDay()];
    const translatedMonths = cms[70237].split(',');
    const monthName = translatedMonths[date.getMonth()];

    if (!includeYear) {
        return showDayOfWeek
            ? cms[94110].replace('{day_name}', dayName).replace('{days}', days.toString()).replace('{month_name}', monthName)
            : cms[125670].replace('{days}', days.toString()).replace('{month_name}', monthName);
    }
    return showDayOfWeek
        ? cms[94111].replace('{day_name}', dayName).replace('{days}', days.toString()).replace('{month_name}', monthName).replace('{years}', years.toString())
        : cms[125671].replace('{days}', days.toString()).replace('{month_name}', monthName).replace('{years}', years.toString());
};

/**
 * Result
 * - If date is today, return "Today"
 * - If date is tomorrow, return "Tomorrow"
 * - Else, return example date in "D MMM" format. Example "1 Mar", "10 Jul", "22 Jul"
 */
export const getFormattedDateWithTodayTomorrow = (date: Date, cms: CmsValue) => {
    if (isToday(date)) return cms[72774];
    if (isTomorrow(date)) return cms[72780];
    return getFormattedDate(date, cms, false, false);
};

export const convertTimeTo12HourFormat = (timeStr = ''): { time: string; meridiem: string } => {
    if (!timeStr) return { time: '', meridiem: '' };

    const timeAsArray = timeStr.split(':').map((t) => parseInt(t, 10));
    const hours = timeAsArray[0];
    const minutes = timeAsArray[1] || 0;
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = (minutes < 10 ? `0${minutes}` : `${minutes}0`).substring(0, 2);

    return { time: `${formattedHours}:${formattedMinutes}`, meridiem: hours < 12 || hours === 24 ? 'AM' : 'PM' };
};

export const setTime = (dateTime: Date, targetDateTime: Date): Date => {
    return set(dateTime, {
        hours: targetDateTime.getHours(),
        minutes: targetDateTime.getMinutes(),
        seconds: targetDateTime.getSeconds(),
    });
};

export const getDateDiff = (endDate: string, startDate: string): number => {
    return differenceInCalendarDays(endDate, startDate) + 1;
};

export const isSameDate = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
};

export const getDateRangeDisplay = (cms: CmsValue, travelDate?: Date, endDate?: Date): string => {
    if (travelDate && endDate && !isSameDay(travelDate, endDate)) {
        return `${getFormattedDate(travelDate, cms, true, false)} - ${getFormattedDate(endDate, cms, true, false)}`;
    }
    if (travelDate) {
        return `${getFormattedDate(travelDate, cms, true, false)}`;
    }
    return '';
};

export const getDateWithTimeInYmdFormat = (date?: Date): string => {
    if (!date) return '';
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};
